import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ConversationStatus, LeadStatus } from '@conversia/shared-types';
import { listarCartera, type FilaDeCartera } from '@/api/cartera.api';
import { FichaDelContacto } from '@/components/contacts/ContactDetailsPanel';
import { HojaDePestana, PestanaSinRegistro } from '@/components/pestanas/HojaDePestana';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { useAbrirChat } from '@/hooks/useAbrirChat';
import { useAsignacion } from '@/hooks/useAsignacion';
import { claveDe } from '@/lib/pestanas';
import { abrirPestana, usePestanas } from '@/store/pestanas.store';
import { AccionesDelContacto } from './AccionesDelContacto';
import { FuenteDelCliente } from './FuenteDelCliente';
import { TarjetaDeProgreso } from './TarjetaDeProgreso';

/**
 * LA FICHA DEL CLIENTE (tanda 6): lo que abre «Ver ficha». Arriba, la tarjeta con su línea
 * de progreso; debajo, la ficha de siempre (la misma del panel de Chats); al pie, «Abrir el
 * chat» y «Asignar…».
 *
 * Desde T12 no es una ventana encima de la pantalla: se abre como una PESTAÑA arriba.
 * `VentanaDeFicha` es lo que montan «Clientes» y Athena (no dibuja nada: abre la pestaña) y
 * `ClienteEnPestana` es la ficha en sí, la que pinta el anfitrión de pestañas.
 */
export function VentanaDeFicha({
  fila,
  abierta,
  onCerrar,
}: {
  fila: FilaDeCartera;
  /** Ya no hace falta (la pestaña las saca de la lista de Clientes); se deja para no tocar a quien la monta. */
  etapas?: readonly string[];
  abierta: boolean;
  onCerrar: () => void;
}) {
  const { contactId, nombre } = fila;
  useEffect(() => {
    if (!abierta) return;
    abrirPestana({ tipo: 'cliente', id: contactId, titulo: nombre });
    onCerrar();
  }, [abierta, contactId, nombre, onCerrar]);
  return null;
}

/**
 * La ficha de un cliente, como contenido de su pestaña. La fila y las etapas salen de la
 * misma consulta que «Clientes» (`['cartera']`): lo que se cambia en la ficha refresca esa
 * caché y la tarjeta de arriba se mueve sola.
 */
export function ClienteEnPestana({ contactId, onCerrar }: { contactId: string; onCerrar: () => void }) {
  const qc = useQueryClient();
  const cartera = useQuery({ queryKey: ['cartera'], queryFn: listarCartera });
  const fila = cartera.data?.filas.find((f) => f.contactId === contactId);

  // Lo que se cambió en la ficha se ve al volver a la lista.
  // Solo la lista (`exact`): la salud de las fuentes (['cartera', 'fuentes']) se refresca cuando cambia una fuente, y al
  // cerrar sesión pedirla otra vez daría 401.
  useEffect(() => () => void qc.invalidateQueries({ queryKey: ['cartera'], exact: true }), [qc]);

  // Si le cambian el nombre, la pestaña lo sigue.
  const nombre = fila?.nombre;
  useEffect(() => {
    if (nombre) usePestanas.getState().renombrarPestana(claveDe('cliente', contactId), nombre);
  }, [contactId, nombre]);

  if (fila) return <FichaEnPestana fila={fila} etapas={cartera.data?.etapas ?? []} />;
  if (cartera.isPending) return <SpinnerOverlay label="Abriendo la ficha…" />;
  return (
    <PestanaSinRegistro
      nombre="Ficha del cliente"
      mensaje={cartera.isError ? 'No se pudo abrir la ficha. Revisa tu conexión y vuelve a abrirla.' : 'Este cliente ya no está en tu lista: lo borraron o lo unieron con otro.'}
      onCerrar={onCerrar}
    />
  );
}

function FichaEnPestana({ fila, etapas }: { fila: FilaDeCartera; etapas: readonly string[] }) {
  const [ahora] = useState(() => new Date());
  const conv = fila.conversacion;
  const asignacion = useAsignacion({ activo: Boolean(conv) });
  const abrirChat = useAbrirChat()(fila);

  return (
    <HojaDePestana nombre="Ficha del cliente" ancho="max-w-4xl">
      {/* El nombre va en la pestaña; el título queda para quien navega por encabezados. */}
      <h2 className="sr-only">Ficha del cliente</h2>
      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-4 md:p-5">
        {/* En su propia caja: la tarjeta mide `h-full` (para la rejilla de «Tarjetas») y aquí se comería la hoja. */}
        <div>
          <TarjetaDeProgreso fila={fila} etapas={etapas} ahora={ahora} compacta onAbrirChat={abrirChat} />
        </div>
        {/* La `key` reinicia el campo cuando cambia el cliente o su fuente guardada (revisión de Codex). */}
        <FuenteDelCliente key={`${fila.contactId}:${fila.fuente}`} fila={fila} />
        <FichaDelContacto
          contactId={fila.contactId}
          conversationId={conv?.id}
          leadStatus={(conv?.temperatura as LeadStatus | null) ?? null}
          conversationStatus={conv?.estado as ConversationStatus | undefined}
          dosColumnas
        />
      </div>
      <div className="flex shrink-0 items-center justify-end gap-2 border-t border-surface-border p-4 md:px-5 dark:border-slate-800">
        <AccionesDelContacto
          fila={fila}
          asignado={asignacion.asignadoDe(fila)}
          agentes={asignacion.agentes}
          personas={asignacion.personas}
          ocupado={asignacion.ocupado}
          onAsignar={(valor) => asignacion.asignar(fila, valor)}
          onAbrirChat={() => abrirChat?.()}
        />
      </div>
    </HojaDePestana>
  );
}
